import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, QrCode } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
//   PER-TABLE QR CODES — printable, persistent stickers for each table
// ============================================================================
const PerTableQRCodesCard = ({ tables, rooms }) => {
  const [qrMap, setQrMap] = useState({}); // { table_id: { order_url, session_code } }
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState("All");

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    // Fan-out: fetch the persistent QR URL for every table in parallel.
    // The endpoint is public + idempotent (auto-creates session on first hit).
    const fetchAll = async () => {
      const results = await Promise.allSettled(
        tables.map((t) => axios.get(`${API}/tables/${t.id}/qr-url`))
      );
      const map = {};
      results.forEach((r, i) => {
        if (r.status === "fulfilled") map[tables[i].id] = r.value.data;
      });
      if (!ignore) setQrMap(map);
      if (!ignore) setLoading(false);
    };
    if (tables.length > 0) fetchAll();
    else setLoading(false);
    return () => { ignore = true; };
  }, [tables]);

  const visible = selectedRoom === "All"
    ? tables
    : tables.filter((t) => t.room === selectedRoom);

  const printOne = (table) => {
    const data = qrMap[table.id];
    if (!data) return;
    openPrintWindow([{ table, data }]);
  };

  const printAll = () => {
    const rows = visible
      .map((t) => ({ table: t, data: qrMap[t.id] }))
      .filter((r) => r.data);
    if (rows.length === 0) {
      toast.error("No QR codes ready to print yet");
      return;
    }
    openPrintWindow(rows);
  };

  return (
    <Card data-testid="per-table-qr-card">
      <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <QrCode size={20} /> Per-Table QR Codes
          </CardTitle>
          <CardDescription>
            Print one QR sticker per table. Customers scan to start a self-order session — the code is persistent, so you only need to print once.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-44">
            <Select value={selectedRoom} onValueChange={setSelectedRoom}>
              <SelectTrigger data-testid="qr-room-filter">
                <SelectValue placeholder="All rooms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All rooms ({tables.length})</SelectItem>
                {rooms.map((r) => (
                  <SelectItem key={r.id} value={r.name}>
                    {r.name} ({tables.filter((t) => t.room === r.name).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={printAll}
            disabled={loading || visible.length === 0}
            className="gap-2"
            data-testid="print-all-qr-btn"
          >
            <Printer size={16} /> Print All
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : visible.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground rounded-lg bg-secondary/30">
            No tables in this room yet. Add tables in the Floor Layout tab first.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {visible.map((table) => {
              const data = qrMap[table.id];
              return (
                <div
                  key={table.id}
                  className="rounded-lg border border-border bg-white p-3 flex flex-col items-center text-center gap-2"
                  data-testid={`qr-card-${table.id}`}
                >
                  <div className="font-heading font-medium text-sm">{table.name}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {table.room}
                  </div>
                  <div className="bg-white border border-border rounded-md p-2">
                    {data ? (
                      <QRCodeSVG
                        value={data.order_url}
                        size={120}
                        level="M"
                        includeMargin={false}
                        fgColor="#2D2926"
                        data-testid={`qr-svg-${table.id}`}
                      />
                    ) : (
                      <div className="w-[120px] h-[120px] flex items-center justify-center text-xs text-muted-foreground">
                        Unavailable
                      </div>
                    )}
                  </div>
                  {data && (
                    <div className="font-mono text-base font-semibold text-primary tracking-widest">
                      {data.session_code}
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => printOne(table)}
                    disabled={!data}
                    className="w-full gap-1"
                    data-testid={`print-qr-${table.id}`}
                  >
                    <Printer size={14} /> Print
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Open a print window with one card per row.  Uses api.qrserver.com so the
// rendered popup is a plain HTML document (no React, no fonts pre-load).
const openPrintWindow = (rows) => {
  const win = window.open("", "_blank", "width=900,height=1100");
  const cards = rows
    .map(({ table, data }) => `
      <div class="card">
        <div class="name">${escapeHtml(table.name)}</div>
        <div class="room">${escapeHtml(table.room || "")}</div>
        <div class="qrbox">
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(data.order_url)}" width="240" height="240" />
        </div>
        <div class="code">${escapeHtml(data.session_code)}</div>
        <div class="tagline">Scan to order from your table</div>
      </div>
    `)
    .join("");
  win.document.write(`<!doctype html><html><head>
    <title>Table QR Codes${rows.length === 1 ? ` — ${escapeHtml(rows[0].table.name)}` : ""}</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&family=Space+Mono:wght@700&display=swap');
      *,*::before,*::after { box-sizing:border-box; }
      body { margin:0; padding:24px; font-family:'Outfit',sans-serif; background:#f5f3ef; color:#2D2926; }
      .grid { display:grid; grid-template-columns:repeat(2, 1fr); gap:18px; }
      .card { background:#fff; border:2px solid #E2DED7; border-radius:16px; padding:20px 16px; text-align:center; break-inside:avoid; page-break-inside:avoid; }
      .name { font-size:24px; font-weight:700; margin-bottom:2px; }
      .room { font-size:11px; text-transform:uppercase; letter-spacing:1.5px; color:#8C8883; margin-bottom:14px; }
      .qrbox { display:inline-block; padding:12px; background:#fff; border:1px solid #E2DED7; border-radius:12px; }
      .code { margin-top:12px; font-family:'Space Mono',monospace; font-weight:700; font-size:28px; letter-spacing:8px; color:#B85D43; }
      .tagline { margin-top:6px; font-size:12px; color:#8C8883; }
      @media print {
        body { background:#fff; padding:0; }
        .grid { grid-template-columns:repeat(2, 1fr); gap:0; }
        .card { border:1px dashed #8C8883; border-radius:0; margin:0; padding:24px 16px; }
      }
    </style></head><body>
    <div class="grid">${cards}</div>
    <script>window.onload = () => { setTimeout(() => window.print(), 300); };</script>
  </body></html>`);
  win.document.close();
};

const escapeHtml = (s = "") =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));


export default PerTableQRCodesCard;

